import { defineQuiz } from "./_schema";
import type { Question } from "./_schema";
import { igniteQuiz } from "./ignite";
import { deriveQuizV6EsProfile } from "./quiz-v6-es-profile";

/**
 * QUIZ-V6-ES -- Spanish (US) localization of the ignite quiz (quiz-v6).
 *
 * Question ids and option `value` ids are byte-identical to ignite's: they are
 * the deriver + analytics contract (CLAUDE.md rule 22), so keeping them lets
 * this quiz share `branching` and `classifier` BY REFERENCE and makes its
 * walker output equal ignite's baseline by construction (12 questions,
 * 5004 terminal paths, same persona counts). Only labels are Spanish; emoji,
 * `image.src`, `animation`, and `autoAdvanceMs` are copied unchanged.
 *
 * `deriveProfile` is the one function that is wrapped rather than shared,
 * because it carries the Spanish verdict copy pack. It rides the deriver's
 * opt-in `copy` seam, so every enum field it returns is identical to
 * ignite's (asserted in tests/quizzes/quiz-v6-es-profile.test.ts).
 *
 * `experiment` is intentionally dropped -- same rationale as quiz-v6-gl /
 * quiz-v6-al: Spanish traffic must not contaminate the MM-0064 English test
 * population, and without the block the widget never waits on
 * window.igData (rule 24).
 *
 * Register: neutral Latin American Spanish, tu (informal). All copy below is
 * transcribed verbatim from the "Approved copy" section of
 * docs/specs/2026-07-28-quiz-v6-es-design.md -- keep the two in sync.
 */
const questions: Question[] = [
  // S1 age
  {
    id: "age",
    type: "single",
    prompt: "¿Cuántos años tienes?",
    options: [
      { value: "50-plus", label: "50 años o más" },
      { value: "40-49", label: "40–49 años" },
      { value: "30-39", label: "30–39 años" },
      { value: "20-29", label: "20–29 años" },
    ],
  },
  // S2 concern
  {
    id: "concern",
    type: "single",
    prompt: "¿Cuál es tu mayor preocupación sobre tu salud masculina en este momento?",
    options: [
      { value: "energy", label: "Nivel de energía", emoji: "⚡" },
      { value: "muscle", label: "Masa muscular y fuerza", emoji: "💪" },
      { value: "sex-drive", label: "Deseo y desempeño sexual", emoji: "🔥" },
      { value: "focus", label: "Concentración y claridad mental", emoji: "🧠" },
      { value: "weight-loss", label: "Pérdida de peso", emoji: "⚖️" },
    ],
  },
  // S3 stress-symptoms
  {
    id: "stress-symptoms",
    type: "single",
    prompt:
      "¿Tienes síntomas de estrés durante el día? Como sentirte cansado, irritable, con la mente nublada o de mal humor.",
    options: [
      { value: "yes", label: "Sí" },
      { value: "no", label: "No" },
    ],
  },
  // S4 energy-3pm
  {
    id: "energy-3pm",
    type: "single",
    prompt: "¿Cómo está tu energía a las 3 de la tarde comparada con cuando te despiertas?",
    options: [
      { value: "drained", label: "Completamente agotado, necesito cafeína o una siesta", emoji: "🪫" },
      { value: "afternoon-slump", label: "Bastante más baja, el bajón de la tarde me pega duro", emoji: "😴" },
      { value: "slightly-tired", label: "Algo cansado, todavía funciono pero más lento", emoji: "😐" },
      { value: "same-energy", label: "La misma energía, sin bajón de la tarde", emoji: "⚡" },
    ],
  },
  // S5 when-started
  {
    id: "when-started",
    type: "single",
    prompt: "¿Cuándo empezaste a notar cambios en tu energía y tu impulso?",
    options: [
      { value: "over-year", label: "Hace más de un año" },
      { value: "past-year", label: "En el último año" },
      { value: "few-months", label: "En los últimos meses" },
      { value: "not-sure", label: "No estoy seguro" },
    ],
  },
  // S6 results-want
  {
    id: "results-want",
    type: "single",
    prompt: "¿Qué resultados esperas al aumentar tu testosterona?",
    options: [
      { value: "energy", label: "Más energía que dure todo el día", emoji: "⚡" },
      { value: "muscle", label: "Más masa muscular y fuerza", emoji: "💪" },
      { value: "sex-drive", label: "Mejor deseo y desempeño sexual", emoji: "🔥" },
      { value: "focus", label: "Mejor claridad mental y concentración", emoji: "🧠" },
      { value: "all-of-above", label: "Todo lo anterior", emoji: "✨" },
    ],
  },
  // S7 info-right-place
  {
    id: "info-right-place",
    type: "info",
    heading: "Estás en el lugar correcto.",
    body: [
      "Más de 429,576 hombres han recuperado su testosterona, su impulso y su físico gracias a nuestro nuevo sistema natural y científico de reactivación de la T.",
    ],
    image: {
      src: "/static/images/before-after.gif",
      alt: "Clientes de Mars Men, antes y después",
    },
  },
  // S8 body-composition
  {
    id: "body-composition",
    type: "single",
    prompt: "¿Cómo describirías tu composición corporal actual?",
    options: [
      { value: "lean-muscular", label: "Delgado y musculoso" },
      { value: "average", label: "Complexión promedio" },
      { value: "soft-some-muscle", label: "Blando con algo de músculo" },
      { value: "overweight", label: "Con sobrepeso / fuera de forma" },
    ],
  },
  // S9 sleep-hours
  {
    id: "sleep-hours",
    type: "single",
    prompt: "¿Cuántas horas de sueño de calidad duermes por noche?",
    options: [
      { value: "lt-5", label: "Menos de 5 horas" },
      { value: "5-6", label: "5–6 horas" },
      { value: "7-8", label: "7–8 horas" },
      { value: "gt-8", label: "Más de 8 horas" },
    ],
  },
  // S10 info-analyzing
  {
    id: "info-analyzing",
    type: "info",
    heading: "Analizando tu perfil de salud…",
    body: [
      "Con base en tus respuestas, estamos construyendo una evaluación completa de tu salud hormonal e identificando los factores clave que afectan tu energía y tu vitalidad.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
  // S11 family-history
  {
    id: "family-history",
    type: "single",
    prompt: "¿Tienes antecedentes familiares de diabetes, enfermedad cardíaca o problemas metabólicos?",
    options: [
      { value: "yes", label: "Sí" },
      { value: "no", label: "No" },
      { value: "not-sure", label: "No estoy seguro" },
    ],
    whyWeAsk: {
      heading: "Por qué preguntamos",
      body: "Estas condiciones comparten causas de raíz con la testosterona baja: resistencia a la insulina, inflamación y disfunción metabólica. Los patrones familiares nos ayudan a identificar tus factores de riesgo.",
    },
  },
  // S12 processed-foods
  {
    id: "processed-foods",
    type: "single",
    prompt: "¿Con qué frecuencia comes alimentos procesados?",
    options: [
      { value: "daily", label: "A diario, casi todas mis comidas los incluyen" },
      { value: "several-week", label: "Varias veces a la semana" },
      { value: "occasionally", label: "De vez en cuando, casi siempre como alimentos naturales" },
      { value: "rarely", label: "Casi nunca, los evito" },
    ],
    whyWeAsk: {
      heading: "Por qué preguntamos",
      body: "Los alimentos procesados contienen químicos y aditivos que interfieren con la producción de hormonas. Varios estudios vinculan los alimentos ultraprocesados con niveles más bajos de testosterona.",
    },
  },
];

export const quizV6EsQuiz = defineQuiz({
  id: "quiz-v6-es",
  slug: "quiz-v6-es",
  locale: "es",
  experiment: undefined,
  meta: {
    title: "Qué Está Pasando Realmente Con Tu Testosterona",
    introHeadline: "Descubre Qué Está Pasando Realmente Con Tu Testosterona",
    introAccent: "realmente",
    introBody:
      "Esta evaluación de 2 minutos analiza tus síntomas, tu estilo de vida y tus factores de riesgo para encontrar una solución.",
    testimonial: {
      quote:
        "Me tomó como un minuto. Digamos que mi esposa y yo estamos MUY contentos de que hice este quiz.",
      author: "ADAM, Cliente Verificado",
    },
  },
  questions,
  // Reused untranslated: ignite has no email gate, so `capture` is never
  // rendered (see .claude/submission-and-klaviyo.md). A gated Spanish variant
  // would need this block translated.
  capture: igniteQuiz.capture,
  branching: igniteQuiz.branching,
  classifier: igniteQuiz.classifier,
  deriveProfile: deriveQuizV6EsProfile,
});
